import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox, EmptyBox } from '../components/admin/ui';
import { Tooltip } from '../components/Tooltip';
import { TOOLTIPS, type TooltipKey } from '../lib/tooltips';

/**
 * Tela /risco (cliente): contratos ordenados por score desc, chips de flags,
 * frase explicativa, filtro por empreendimento e flag, paginação 50. Estados
 * vazio/carregando/erro. Tokens canônicos (tema claro, vermelho-risco).
 */

const FLAG_META = {
  parcela_atrasada: { label: 'Parcela atrasada', cor: 'var(--risk)', tip: 'flagParcelaAtrasada' },
  reajuste_em_7d: { label: 'Reajuste em 7 dias', cor: 'var(--risk)', tip: 'flagReajuste7d' },
  reajuste_em_30d: { label: 'Reajuste em 30 dias', cor: 'var(--amber)', tip: 'flagReajuste30d' },
  padrao_piorando: { label: 'Padrão piorando', cor: 'var(--risk)', tip: 'flagPadraoPiorando' },
  indice_incc_faltante: { label: 'INCC faltante', cor: 'var(--ink-3)', tip: 'flagIndiceInccFaltante' },
  saldo_estimado: { label: 'Saldo estimado', cor: 'var(--ink-3)', tip: 'flagSaldoEstimado' },
} as const satisfies Record<string, { label: string; cor: string; tip: TooltipKey }>;
type FlagTipo = keyof typeof FLAG_META;
const FLAGS = Object.keys(FLAG_META) as FlagTipo[];

function scoreCor(s: number): string {
  return s >= 70 ? 'var(--risk)' : s >= 40 ? 'var(--amber)' : 'var(--ink-3)';
}

function Chip({ tipo }: { tipo: FlagTipo }) {
  const meta = FLAG_META[tipo];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: 'var(--surface-2)', color: meta.cor }}
    >
      {meta.label}
      <Tooltip text={TOOLTIPS[meta.tip]} label={`O que é ${meta.label}`} />
    </span>
  );
}

function IconeFiltro({ aberto }: { aberto: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      className={`transition-transform motion-reduce:transition-none ${aberto ? 'rotate-180' : ''}`}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function RiscoPage() {
  const [empreendimento, setEmpreendimento] = useState('');
  const [flag, setFlag] = useState<'' | FlagTipo>('');
  const [page, setPage] = useState(1);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const { data, loading, error, reload } = useAsync(
    () => trpc.risco.list.query({ empreendimento: empreendimento || undefined, flag: flag || undefined, page }),
    [empreendimento, flag, page],
  );

  return (
    <div className="mx-auto max-w-5xl" data-testid="risco-page">
      <h1 className="text-2xl font-semibold text-ink">Contratos em risco</h1>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-3">
        Ordenados por score de risco. Priorize os primeiros.
        <Tooltip text={TOOLTIPS.riscoScore} label="O que é o score de risco" />
      </p>

      {/* Filtros - colapsáveis no mobile (toggle), sempre visíveis no desktop */}
      <button
        type="button"
        onClick={() => setFiltrosAbertos((a) => !a)}
        aria-expanded={filtrosAbertos}
        aria-controls="risco-filtros"
        data-testid="risco-filtros-toggle"
        className="mt-4 flex h-11 items-center gap-1.5 text-sm font-medium text-ink-2 md:hidden"
      >
        Filtros
        <IconeFiltro aberto={filtrosAbertos} />
      </button>
      <div
        id="risco-filtros"
        className={`mt-2 flex-wrap items-end gap-3 md:mt-4 md:flex ${filtrosAbertos ? 'flex' : 'hidden'}`}
      >
        <label className="flex flex-1 flex-col gap-1 text-xs text-ink-3 md:flex-none">
          Empreendimento
          <select
            value={empreendimento}
            onChange={(e) => {
              setEmpreendimento(e.target.value);
              setPage(1);
            }}
            className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-ink md:h-10 md:w-auto"
            data-testid="risco-filtro-empreendimento"
          >
            <option value="">Todos</option>
            {data?.empreendimentos.map((emp) => (
              <option key={emp} value={emp}>
                {emp}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-ink-3 md:flex-none">
          Flag
          <select
            value={flag}
            onChange={(e) => {
              setFlag(e.target.value as '' | FlagTipo);
              setPage(1);
            }}
            className="h-11 w-full rounded-[10px] border border-line bg-surface px-3 text-sm text-ink md:h-10 md:w-auto"
            data-testid="risco-filtro-flag"
          >
            <option value="">Todas</option>
            {FLAGS.map((f) => (
              <option key={f} value={f}>
                {FLAG_META[f].label}
                {data?.resumoFlags.find((x) => x.tipo === f) ? ` (${data.resumoFlags.find((x) => x.tipo === f)!.n})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4">
        {loading && <Loading />}
        {error && <ErrorBox msg={error} onRetry={reload} />}
        {data && data.items.length === 0 && (
          <EmptyBox titulo="Nenhum contrato em risco" descricao="Rode a varredura de risco ou ajuste os filtros." />
        )}
        {data && data.items.length > 0 && (
          <>
            <div className="mb-2 text-xs text-ink-3">
              {data.total} contrato(s) · página {data.page} de {data.totalPages}
            </div>
            <ul className="space-y-3">
              {data.items.map((it) => (
                <li key={it.contratoId} className="card p-4" data-testid="risco-item">
                  <div className="flex items-start gap-4">
                    <div
                      className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-[12px] text-white"
                      style={{ background: scoreCor(it.score) }}
                    >
                      <span className="num text-lg font-bold leading-none">{it.score}</span>
                      <span className="text-[9px] uppercase tracking-wide">score</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold text-ink">{it.compradorNome}</span>
                        <span className="text-sm text-ink-3">
                          {it.unidade} · {it.empreendimento}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {it.flags.map((f) => (
                          <Chip key={f.tipo} tipo={f.tipo as FlagTipo} />
                        ))}
                      </div>
                      {it.frase && <p className="mt-2 text-sm text-ink-2">{it.frase}</p>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {data.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                <button
                  type="button"
                  disabled={data.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="flex h-11 items-center rounded-[10px] border border-line px-3 text-ink-2 disabled:opacity-40 md:h-9"
                >
                  ← Anterior
                </button>
                <span className="text-ink-3">
                  {data.page} / {data.totalPages}
                </span>
                <button
                  type="button"
                  disabled={data.page >= data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-11 items-center rounded-[10px] border border-line px-3 text-ink-2 disabled:opacity-40 md:h-9"
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
